import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Plan tiers match SITEMAP v1.1 Legend:
 * [L] Lite · [S] Starter · [P] Professional · [E] Enterprise
 */
export type PlanTier = 'lite' | 'starter' | 'professional' | 'enterprise';

export const PLAN_FEATURES: Record<PlanTier, string[]> = {
  lite: ['pos', 'products', 'orders', 'inventory', 'customers', 'staff', 'reports-basic', 'dashboard'],
  starter: [
    'pos', 'products', 'orders', 'inventory', 'customers', 'staff', 'reports-basic', 'dashboard',
    'suppliers', 'purchasing', 'promotions', 'labels', 'e-invoice',
    'workshop', 'consignment', 'buyback', 'exchange', 'pawn', 'loyalty', 'csv-import',
    'payroll', 'referral', 'commission',
  ],
  professional: [
    'pos', 'products', 'orders', 'inventory', 'customers', 'staff', 'reports-basic', 'dashboard',
    'suppliers', 'purchasing', 'promotions', 'labels', 'e-invoice',
    'workshop', 'consignment', 'buyback', 'exchange', 'pawn', 'loyalty', 'csv-import',
    'orders-online', 'multi-store', 'installment', 'reports-advanced', 'audit-log',
    'flash-sales', 'inventory-transfer', 'support', 'payroll',
    'accounting', 'accounting-reports', 'sales-invoices', 'referral',
    'commission', 'crm',
  ],
  enterprise: ['*'],
};

export const PLAN_LABELS: Record<PlanTier, string> = {
  lite: 'Lite',
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
};

export const PLAN_COLORS: Record<PlanTier, string> = {
  lite: 'default',
  starter: 'blue',
  professional: 'gold',
  enterprise: 'red',
};

export const PLAN_RANK: Record<PlanTier, number> = {
  lite: 0,
  starter: 1,
  professional: 2,
  enterprise: 3,
};

/** Employee limits for payroll tier gating — EG-529 */
export const STARTER_LIMIT = 5;
export const PROFESSIONAL_LIMIT = 20;

export interface PlanLimits {
  products: number; // -1 = unlimited
  staffs: number;
  stores: number;
  monthlyOrders: number;
}

// Sync với Quick Reference trong AGENTS.md:
// Lite: 3 users, 1 store · Starter: 8, 2 · Professional: 20, 5 · Enterprise: unlimited
export const PLAN_LIMITS: Record<PlanTier, PlanLimits> = {
  lite: { products: 200, staffs: 3, stores: 100, monthlyOrders: 500 },
  starter: { products: 1_000, staffs: 8, stores: 100, monthlyOrders: 3_000 },
  professional: { products: 10_000, staffs: 20, stores: 100, monthlyOrders: 30_000 },
  enterprise: { products: -1, staffs: -1, stores: -1, monthlyOrders: -1 },
};

export interface PlanUsage {
  products: number;
  staffs: number;
  stores: number;
  monthlyOrders: number;
}

interface PlanState {
  tier: PlanTier;
  expiresAt: string | null;
  trialDaysLeft: number | null;
  graceDaysLeft: number | null;
  features: string[];
  usage: PlanUsage;
  isLoaded: boolean;
}

const initialState: PlanState = {
  tier: 'lite',
  expiresAt: null,
  trialDaysLeft: null,
  graceDaysLeft: null,
  features: [],
  usage: { products: 0, staffs: 0, stores: 0, monthlyOrders: 0 },
  isLoaded: false,
};

const planSlice = createSlice({
  name: 'plan',
  initialState,
  reducers: {
    setPlan: (state, action: PayloadAction<Partial<PlanState>>) => {
      Object.assign(state, action.payload);
      state.isLoaded = true;
    },
    setUsage: (state, action: PayloadAction<PlanUsage>) => {
      state.usage = action.payload;
    },
    clearPlan: () => initialState,
  },
});

export const { setPlan, setUsage, clearPlan } = planSlice.actions;
export default planSlice.reducer;
